"use strict";
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Store } from "../../Store.js";
import {
    GET_CHARACTERS_FOR_PROJECT,
    GET_PROJECTS,
    GET_PROJECTS_FOR_SINGLE_USER,
} from "../../ApiEndpoints.js";

/**
 * Master list of Incantor-owned voices that are available to every project via
 * the shared library.
 */
const INCANTOR_LIBRARY_VOICES = [
    { id: "incantor-aether", name: "Aether", locale: "en-US", character: "Cinematic narrator" },
    { id: "incantor-lyra", name: "Lyra", locale: "en-GB", character: "Lyrical storyteller" },
    { id: "incantor-ember", name: "Ember", locale: "en-US", character: "Youthful adventurer" },
    { id: "incantor-sol", name: "Sol", locale: "es-ES", character: "Warm conversational" },
    { id: "incantor-rhea", name: "Rhea", locale: "fr-FR", character: "Elegant narrator" },
];

const toSafeDate = (input) => {
    if (!input) return null;

    // Reuse an existing Date as-is when it contains a valid timestamp.
    if (input instanceof Date) {
        return Number.isNaN(input.getTime()) ? null : input;
    }

    // Handle Firestore-style timestamp objects with seconds/nanoseconds.
    if (typeof input === "object" && input.seconds != null) {
        const ms = input.seconds * 1000 + (input.nanoseconds || 0) / 1_000_000;
        const date = new Date(ms);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    // Accept numeric epoch timestamps whether in seconds or milliseconds.
    if (typeof input === "number") {
        const ms = input > 1e12 ? input : input * 1000;
        const date = new Date(ms);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    // Interpret ISO-8601 date strings or other parseable formats.
    if (typeof input === "string") {
        const date = new Date(input);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    // Anything else is unsupported and maps to null.
    return null;
};

const VoiceCatalogue = ({ onVoiceSelect }) => {
    const { state } = useContext(Store);
    const { userInfo } = state;
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("project");
    const [projects, setProjects] = useState([]);
    const [projectLoading, setProjectLoading] = useState(false);
    const [projectError, setProjectError] = useState(null);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [characters, setCharacters] = useState([]);
    const [characterLoading, setCharacterLoading] = useState(false);
    const [characterError, setCharacterError] = useState(null);

    // Order projects with newest first while falling back to alphabetical names.
    const sortedProjects = useMemo(() => {
        return [...projects].sort((a, b) => {
            if (a.createdAt && b.createdAt) {
                return b.createdAt - a.createdAt;
            }
            if (a.createdAt) return -1;
            if (b.createdAt) return 1;
            return a.name.localeCompare(b.name);
        });
    }, [projects]);

    // Resolve the currently highlighted project from the memoized list.
    const selectedProject = useMemo(
        () => sortedProjects.find((project) => project.id === selectedProjectId) ?? null,
        [sortedProjects, selectedProjectId],
    );

    useEffect(() => {
        // Reset the project data while the user context is missing.
        if (!userInfo?.id) {
            setProjects([]);
            setSelectedProjectId(null);
            return;
        }

        let isMounted = true;
        const fetchProjects = async () => {
            setProjectLoading(true);
            setProjectError(null);

            try {
                // Admins may list every project in the system.
                if (userInfo.isAdmin) {
                    const { data } = await axios.get(GET_PROJECTS);
                    const items =
                        data?.projectsArray?.map((project) => ({
                            id: project.id,
                            name: project.name || "Untitled project",
                            originalLanguage:
                                project.originalLang || project.originalLanguage || project.sourceLanguage || null,
                            languages: Array.isArray(project.languages)
                                ? project.languages.filter(Boolean)
                                : Array.isArray(project.finalLang)
                                ? project.finalLang.filter(Boolean)
                                : [],
                            createdAt: toSafeDate(project.creationDate) || toSafeDate(project.createdAt),
                            description: project.description || project.synopsis || project.summary || "",
                        })) ?? [];

                    if (isMounted) {
                        setProjects(items);
                        setSelectedProjectId((prev) => {
                            if (prev && items.some((item) => item.id === prev)) {
                                return prev;
                            }
                            return items[0]?.id ?? null;
                        });
                    }
                } else {
                    // Non-admins fetch projects tied to their account.
                    const { data } = await axios.post(GET_PROJECTS_FOR_SINGLE_USER, {
                        id: userInfo.id,
                        searchTerm: "",
                    });

                    const projectMap = new Map();
                    data?.projectsArray?.forEach((entry) => {
                        if (!entry?.id) return;

                        const existing = projectMap.get(entry.id);
                        const languages = Array.isArray(entry.languages)
                            ? entry.languages.filter(Boolean)
                            : Array.isArray(entry.finalLang)
                            ? entry.finalLang.filter(Boolean)
                            : [];
                        const createdAt =
                            toSafeDate(entry.creationDate) || toSafeDate(entry.createdAt) || toSafeDate(entry.dateCreated);
                        const description = entry.synopsis || entry.description || entry.projectSynopsis || "";
                        const originalLanguage =
                            entry.originalLang || entry.originalLanguage || entry.sourceLanguage || entry.language;

                        if (existing) {
                            // Merge details when the API returns duplicate project IDs.
                            existing.languages = Array.from(new Set([...existing.languages, ...languages])).filter(Boolean);
                            existing.createdAt = existing.createdAt || createdAt;
                            existing.description = existing.description || description;
                            if (!existing.originalLanguage && originalLanguage) {
                                existing.originalLanguage = originalLanguage;
                            }
                        } else {
                            // Create a fresh project entry when we have not seen the ID yet.
                            projectMap.set(entry.id, {
                                id: entry.id,
                                name: entry.name || "Untitled project",
                                languages: languages.filter(Boolean),
                                createdAt,
                                description,
                                originalLanguage,
                            });
                        }
                    });

                    const items = Array.from(projectMap.values());
                    if (isMounted) {
                        setProjects(items);
                        setSelectedProjectId((prev) => {
                            if (prev && items.some((item) => item.id === prev)) {
                                return prev;
                            }
                            return items[0]?.id ?? null;
                        });
                    }
                }
            } catch (error) {
                console.error("Failed to load projects", error);
                if (isMounted) {
                    setProjectError("We couldn't load your projects. Please try again later.");
                    setProjects([]);
                    setSelectedProjectId(null);
                }
            } finally {
                // Ensure the spinner clears once the fetch settles.
                if (isMounted) {
                    setProjectLoading(false);
                }
            }
        };

        fetchProjects();

        return () => {
            isMounted = false;
        };
    }, [userInfo?.id, userInfo?.isAdmin]);

    useEffect(() => {
        // Clear out characters while no project is selected.
        if (!selectedProjectId) {
            setCharacters([]);
            return;
        }

        let isMounted = true;
        const fetchCharacters = async () => {
            setCharacterLoading(true);
            setCharacterError(null);

            try {
                // Retrieve characters and their assigned voices for the chosen project.
                const { data } = await axios.post(GET_CHARACTERS_FOR_PROJECT, {
                    projectId: selectedProjectId,
                });

                const items = data?.characters ?? [];
                if (isMounted) {
                    setCharacters(items);
                }
            } catch (error) {
                console.error("Failed to load characters", error);
                if (isMounted) {
                    setCharacterError("We couldn't load voices for this project.");
                    setCharacters([]);
                }
            } finally {
                // Stop the character loading spinner regardless of outcome.
                if (isMounted) {
                    setCharacterLoading(false);
                }
            }
        };

        fetchCharacters();

        return () => {
            isMounted = false;
        };
    }, [selectedProjectId]);

    // Track which tab the user is viewing inside the voice catalogue.
    const handleTabChange = (event, value) => {
        setActiveTab(value);
    };

    // Allow switching the highlighted project to drive the voice list.
    const handleProjectSelect = (projectId) => {
        setSelectedProjectId(projectId);
    };

    const handleVoiceUse = (voice) => {
        if (!voice || !selectedProjectId) {
            return;
        }

        const voiceId = voice.voice_id || voice.voiceId || voice.id;
        const voiceName = voice.displayName || voice.voiceName || voice.name || voiceId;

        if (onVoiceSelect) {
            onVoiceSelect({
                projectId: selectedProjectId,
                voiceId,
                voiceName,
            });
        }

        navigate("../voice-sandbox");
    };

    // Build a concise descriptor combining original and final language details.
    const renderProjectLanguages = (project) => {
        const { originalLanguage, languages = [] } = project;
        if (!originalLanguage && (!languages || languages.length === 0)) {
            return project.description || null;
        }

        const finalLanguages = languages.length > 0 ? languages.join(", ") : "—";
        const originalLabel = originalLanguage ? `Original: ${originalLanguage}` : null;
        const finalLabel = `Final: ${finalLanguages}`;

        return [originalLabel, finalLabel, project.description]
            .filter(Boolean)
            .join(" \u2022 ");
    };

    // Render a compact bullet list capturing key voice metadata.
    const renderVoiceSummary = (voice) => {
        const parts = [];
        const voiceId = voice.voice_id || voice.voiceId || voice.id;
        const label = voice.displayName || voice.voiceName || voice.name;
        const emotion = voice.emotion || voice.style;
        const provider = voice.vendor || voice.provider || voice.engineType || voice.engine;
        const locale = voice.locale || voice.language;

        if (emotion) parts.push(emotion);
        if (label && label !== emotion) parts.push(label);
        if (voiceId && !parts.includes(voiceId)) parts.push(voiceId);
        if (provider) parts.push(provider);
        if (locale) parts.push(locale);

        return parts.join(" · ");
    };

    return (
        <Box>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="Voice catalogue tabs"
                sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
                <Tab value="project" label="Project Voices" />
                <Tab value="library" label="Library Voices" />
            </Tabs>

            {activeTab === "project" && (
                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
                    <Box sx={{ flexBasis: { md: "30%" }, maxWidth: { md: 320 } }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Projects
                        </Typography>
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                minHeight: 160,
                                maxHeight: "calc(100vh - 240px)",
                                overflowY: "auto",
                                bgcolor: "background.paper",
                            }}
                        >
                            {projectLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : projectError ? (
                                <Typography color="error" sx={{ p: 2 }}>
                                    {projectError}
                                </Typography>
                            ) : sortedProjects.length === 0 ? (
                                <Typography sx={{ p: 2 }}>
                                    No projects available yet. Once you have localization projects, they will appear here.
                                </Typography>
                            ) : (
                                <List disablePadding>
                                    {sortedProjects.map((project) => (
                                        <ListItem disablePadding key={project.id}>
                                            <ListItemButton
                                                selected={project.id === selectedProjectId}
                                                onClick={() => handleProjectSelect(project.id)}
                                                sx={{ alignItems: "flex-start" }}
                                            >
                                                <ListItemText
                                                    primary={project.name}
                                                    secondary={renderProjectLanguages(project)}
                                                    primaryTypographyProps={{ fontWeight: 600 }}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Available Voices
                        </Typography>
                        <Box
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                minHeight: 160,
                                maxHeight: "calc(100vh - 240px)",
                                overflowY: "auto",
                                bgcolor: "background.paper",
                            }}
                        >
                            {!selectedProject ? (
                                <Typography sx={{ p: 2 }}>
                                    Select a project to review its voice assignments.
                                </Typography>
                            ) : characterLoading ? (
                                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : characterError ? (
                                <Typography color="error" sx={{ p: 2 }}>
                                    {characterError}
                                </Typography>
                            ) : characters.length === 0 ? (
                                <Typography sx={{ p: 2 }}>
                                    No characters or voices configured for this project yet.
                                </Typography>
                            ) : (
                                <List disablePadding>
                                    {characters.map((character, index) => {
                                        const projectVoices = Array.isArray(character.voices)
                                            ? character.voices.filter(Boolean)
                                            : [];

                                        return (
                                            <React.Fragment key={character.speaker || character.id || index}>
                                                <ListItem alignItems="flex-start">
                                                    <ListItemText
                                                        primary={character.displayName || character.speaker || "Unnamed character"}
                                                        secondary={
                                                            <Box
                                                                component="span"
                                                                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                                                            >
                                                                {projectVoices.length > 0 ? (
                                                                    projectVoices.map((voice, voiceIndex) => {
                                                                        const line = renderVoiceSummary(voice);
                                                                        const voiceId = voice.voice_id || voice.voiceId || voice.id;

                                                                        return (
                                                                            <Box
                                                                                key={`${
                                                                                    character.speaker || character.id || index
                                                                                }-voice-${voiceIndex}`}
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    alignItems: {
                                                                                        xs: "flex-start",
                                                                                        sm: "center",
                                                                                    },
                                                                                    gap: 2,
                                                                                    justifyContent: "space-between",
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    color="text.secondary"
                                                                                    sx={{ flexGrow: 1 }}
                                                                                >
                                                                                    {line || "Voice details unavailable."}
                                                                                </Typography>
                                                                                <Button
                                                                                    size="small"
                                                                                    variant="contained"
                                                                                    onClick={() => handleVoiceUse(voice)}
                                                                                    disabled={!voiceId || !selectedProjectId}
                                                                                >
                                                                                    Use
                                                                                </Button>
                                                                            </Box>
                                                                        );
                                                                    })
                                                                ) : (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No voices assigned yet.
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                        primaryTypographyProps={{ fontWeight: 600 }}
                                                        secondaryTypographyProps={{ component: "div" }}
                                                    />
                                                </ListItem>
                                                {index < characters.length - 1 && <Divider component="li" />}
                                            </React.Fragment>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Box>
                </Box>
            )}

            {activeTab === "library" && (
                <Box>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Incantor Library Voices
                    </Typography>
                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
                        <List disablePadding>
                            {INCANTOR_LIBRARY_VOICES.map((voice, index) => (
                                <React.Fragment key={voice.id}>
                                    <ListItem alignItems="flex-start">
                                        <ListItemText
                                            primary={`${voice.name} · ${voice.locale}`}
                                            secondary={voice.character}
                                            primaryTypographyProps={{ fontWeight: 600 }}
                                        />
                                    </ListItem>
                                    {index < INCANTOR_LIBRARY_VOICES.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default VoiceCatalogue;
